import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { Link } from 'react-router-dom';
import SimpleMap from './SimpleMap';

// Custom marker icon for driver - using a simple colored circle
const driverIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMTIiIGN5PSIxMiIgcj0iMTAiIGZpbGw9IiMyNTYzZWYiLz4KPGNpcmNsZSBjeD0iMTIiIGN5PSIxMiIgcj0iNiIgZmlsbD0id2hpdGUiLz4KPC9zdmc+',
  iconSize: [24, 24],
  iconAnchor: [12, 12],
  popupAnchor: [0, -12]
});

// Component to update map center when driver location changes
const MapUpdater = ({ center }) => {
  const map = useMap();
  
  useEffect(() => {
    if (center && center[0] && center[1]) {
      map.setView(center, 15);
    }
  }, [center, map]);
  
  return null;
};

const DriverMap = ({ driver, height = '400px' }) => {
  const [useSimpleMap, setUseSimpleMap] = useState(false);
  
  const formatTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  };

  if (!driver.location || !driver.location.coordinates || driver.location.coordinates.length < 2) {
    return (
      <div className="bg-gray-100 rounded-lg flex items-center justify-center" style={{ height }}>
        <div className="text-center">
          <div className="w-12 h-12 bg-gray-300 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
          <p className="text-gray-600">No location data available</p>
          <p className="text-sm text-gray-500 mt-2">
            Driver location is not currently available
          </p>
        </div>
      </div>
    );
  }

  // If simple map is enabled, use the fallback
  if (useSimpleMap) {
    return <SimpleMap driver={driver} height={height} />;
  }

  const currentLocation = [driver.location.coordinates[1], driver.location.coordinates[0]];

  try {
    return (
      <div style={{ height, width: '100%' }}>
        <MapContainer
          center={currentLocation}
          zoom={15}
          style={{ height: '100%', width: '100%' }}
          zoomControl={true}
          scrollWheelZoom={true}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='© OpenStreetMap contributors'
            maxZoom={19}
            subdomains={['a', 'b', 'c']}
          />
          
          {/* Driver's current location */}
          <Marker
            position={currentLocation}
            icon={driverIcon}
          >
            <Popup>
              <div className="text-center">
                <h3 className="font-medium text-gray-900">{driver.name}</h3>
                <p className="text-sm text-gray-600">Current Location</p>
                <p className="text-xs text-gray-500 mt-1">
                  {/* The position's own time first: liveStats only follows trip pings, so
                      it can be old for a driver who is online but not on a trip. */}
                  {formatTime(driver.location?.updatedAt || driver.liveStats?.lastLocationUpdatedOn)}
                </p>
                {driver.location.tripId && (
                  <Link 
                    to={`/trips/${driver.location.tripId}`}
                    className="text-xs text-primary-600 hover:text-primary-800 block mt-2"
                  >
                    View Current Trip
                  </Link>
                )}
              </div>
            </Popup>
          </Marker>
          
                  {/* Home location marker */}
        {driver.homeLocation && driver.homeLocation.coordinates && (
          <Marker
            position={[driver.homeLocation.coordinates[1], driver.homeLocation.coordinates[0]]}
            icon={new L.Icon({
              iconUrl: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTEyIDJDNi40OCAyIDIgNi40OCAyIDEyQzIgMTcuNTIgNi40OCAyMiAxMiAyMkMxNy41MiAyMiAyMiAxNy41MiAyMiAxMkMyMiA2LjQ4IDE3LjUyIDIgMTIgMloiIGZpbGw9IiMxMGJhNmEiLz4KPHBhdGggZD0iTTEyIDZDNi40OCA2IDIgMTAuNDggMiAxNkMyIDIxLjUyIDYuNDggMjYgMTIgMjZDMjEuNTIgMjYgMjYgMjEuNTIgMjYgMTZDMjYgMTAuNDggMjEuNTIgNiAxMiA2WiIgZmlsbD0iIzQmY2E4ZCIvPgo8L3N2Zz4K',
              iconSize: [24, 24],
              iconAnchor: [12, 12],
              popupAnchor: [0, -12]
            })}
          >
            <Popup>
              <div className="text-center">
                <h3 className="font-medium text-gray-900">{driver.name}</h3>
                <p className="text-sm text-gray-600">Home Location</p>
                <p className="text-xs text-gray-500 mt-1">
                  {driver.homeLocation.addressName}
                </p>
              </div>
            </Popup>
          </Marker>
        )}
          
          {/* Update map center when location changes */}
          <MapUpdater center={currentLocation} />
        </MapContainer>
      </div>
    );
  } catch (error) {
    console.error('Error rendering map:', error);
    // Fallback to simple map
    setUseSimpleMap(true);
    return <SimpleMap driver={driver} height={height} />;
  }
};

export default DriverMap; 